/**
 * Turns the server saved because this client was not there to receive them
 * (tdd-0090 Phase 3).
 *
 * When a chat turn completes and its socket has already closed, the LB writes
 * the result to `sessions/<id>.pending/<turnId>.json` instead of losing it.
 * This module reads those back on load and folds them into the conversation.
 *
 * ## Why a sidecar and not the session document
 *
 * The storage API has no conditional write — GET returns an ETag but PUT has no
 * `If-Match` — so a server appending to `sessions/<id>.json` would be a
 * read-modify-write race against this client, and losing it costs a
 * conversation. Each pending turn therefore gets its own key, which cannot
 * collide, and merging stays here where it always was. The client remains the
 * merge authority (`mergeSessions`); this adds a source, not a second owner.
 *
 * ## Why it matters more than a missing sentence
 *
 * A recovered turn is usually an image edit, and its visible reply lives
 * entirely in a TOOL RESULT — `componentSegmentsFromToolCalls` extracts the
 * `json-render` block from completed calls, so a turn with no prose still
 * renders. That block is the only thing that ever collects the finished image
 * (tdd-0087), so restoring it is what makes already-paid-for GPU work reachable
 * rather than stranded.
 */

import { storageClient } from '@workspace/ui/hooks/useAppStorage';
import type { StorageItem, StorageItemMeta } from '@workspace/ui/hooks/useAppStorage';
import type { Message, ToolCallWithResult } from '../types';

const APP_NAME = 'chat';

/** Written by the LB; mirrored here rather than shared, as the LB cannot import UI types. */
export interface PendingTurn {
  turnId: string;
  completedAt: string;
  content: string;
  toolCalls: ToolCallWithResult[];
  model?: string;
}

function storagePath(key: string): string {
  return `/v1/storage/${APP_NAME}/${encodeURIComponent(key)}`;
}

export function pendingPrefix(sessionId: string): string {
  return `sessions/${sessionId}.pending/`;
}

/**
 * Rebuild an assistant message from a saved turn.
 *
 * `id` is derived from the turn id rather than freshly generated, so a merge
 * that runs twice — two tabs, a re-render, a failed delete — produces the same
 * message identity and de-duplicates instead of doubling the reply.
 */
export function pendingTurnToMessage(turn: PendingTurn): Message {
  return {
    id: `recovered-${turn.turnId}`,
    role: 'assistant',
    content: turn.content ?? '',
    createdAt: Date.parse(turn.completedAt) || Date.now(),
    ...(turn.model ? { modelId: turn.model } : {}),
    ...(turn.toolCalls?.length ? { toolCalls: turn.toolCalls } : {}),
  };
}

/** List the turns saved for a conversation. Absent or unreadable ⇒ none. */
export async function listPendingTurns(sessionId: string): Promise<PendingTurn[]> {
  let metas: StorageItemMeta[];
  try {
    const { data } = await storageClient.get<{ items: StorageItemMeta[] }>(
      `/v1/storage/${APP_NAME}?prefix=${encodeURIComponent(pendingPrefix(sessionId))}&limit=50`,
    );
    metas = data?.items ?? [];
  } catch {
    // A conversation with nothing pending is the overwhelmingly common case,
    // and recovery is best-effort by nature — never block a load on it.
    return [];
  }

  const turns: PendingTurn[] = [];
  for (const meta of metas) {
    try {
      const { data: item } = await storageClient.get<StorageItem<PendingTurn>>(
        storagePath(meta.key),
      );
      if (item?.data?.turnId) turns.push(item.data);
    } catch {
      /* skip an unreadable one rather than losing the rest */
    }
  }

  // Oldest first, so a conversation that lost several turns restores in order.
  return turns.sort((a, b) => a.completedAt.localeCompare(b.completedAt));
}

/** Remove a turn once it has been merged into the stored conversation. */
export async function deletePendingTurn(
  sessionId: string,
  turnId: string,
): Promise<void> {
  await storageClient
    .delete(storagePath(`${pendingPrefix(sessionId)}${turnId}.json`))
    .catch(() => {
      // Leaving one behind is harmless — the merge is idempotent on message id,
      // so it would be folded in again to the same result. Failing the load
      // because cleanup failed would not be.
    });
}

/**
 * Fold saved turns into a conversation's messages.
 *
 * Idempotent: a turn already present (by derived id) is skipped, so merging
 * twice cannot duplicate a reply. Appended rather than interleaved — these
 * turns completed after everything the client holds, which is precisely why
 * the client never saw them.
 */
export function mergePendingTurns(
  messages: Message[],
  turns: PendingTurn[],
): Message[] {
  if (turns.length === 0) return messages;

  const seen = new Set(messages.map((m) => m.id));
  const recovered = turns
    .map(pendingTurnToMessage)
    .filter((m) => !seen.has(m.id));

  return recovered.length ? [...messages, ...recovered] : messages;
}
